import React, { ComponentPropsWithoutRef, forwardRef } from "react"
import { disabledVariants } from "../../styles/utils/disabled"
import { classNames } from "../../utils/classNames"
import { AvatarIconProps, AvatarProps } from "../Avatar"
import { FlexColumn } from "../FlexColumn"
import { Item, ItemProps } from "../Item"
import { List, ListProps } from "../List"
import { TimelineSkeleton } from "./TimelineSkeleton"

type TimelineProps = ListProps

const TimelineBase = forwardRef<HTMLUListElement, TimelineProps>(
  function TimelineBase({ className, children, ...props }, ref) {
    return (
      <List
        className={classNames("w-full", className)}
        ref={ref}
        showBorder={false}
        variant="framed"
        {...props}
      >
        {children}
      </List>
    )
  },
)

type TimelineItemProps = {
  active?: boolean
} & ItemProps

export const TimelineItem = forwardRef<HTMLLIElement, TimelineItemProps>(
  function TimelineItem({ active, children, ...props }, ref) {
    return (
      <FlexColumn
        className={classNames(
          // Separator styles
          "after:my-1.5 after:ml-3 after:block after:h-4 after:w-0.5 after:rounded-full after:bg-text-primary",
          "last:after:hidden",
        )}
      >
        <List.Item
          className={classNames(
            "gap-4",
            disabledVariants({ disabled: !active }),
          )}
          ref={ref}
          variant="unstyled"
          {...props}
        >
          {children}
        </List.Item>
      </FlexColumn>
    )
  },
)

export const TimelineAvatar = forwardRef<HTMLDivElement, AvatarProps>(
  function TimelineAvatar({ className, ...props }, ref) {
    return (
      <Item.Avatar
        className={classNames(
          "rounded bg-bg-additional-1",
          (props as AvatarIconProps).icon && "p-0.5",
          className,
        )}
        ref={ref}
        size={24}
        {...props}
      />
    )
  },
)

export const TimelineContent = Item.Content

export const TimelineDescription = Item.Description

export const TimelineSide = Item.Side

export const TimelineTitle = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Item.Title>
>(function TimelineTitle(props, ref) {
  return <Item.Title ref={ref} size="sm" {...props} />
})

export const Timeline = Object.assign(TimelineBase, {
  Item: Object.assign(TimelineItem, {
    Avatar: TimelineAvatar,
    Content: TimelineContent,
    Title: TimelineTitle,
    Description: TimelineDescription,
    Side: TimelineSide,
  }),
  Skeleton: TimelineSkeleton,
})
